import { FieldArray, Formik } from 'formik';
import React from 'react';
import * as yup from 'yup';
import toast from '../../../utils/toast';
import validateFormSchema from '../../../utils/validateFormSchema';
import { Block } from '../../layout';
import { Element, List, Section } from './components';

function ContentEditor({ values = {}, structure = { sections: [] }, onSubmit, bindSubmitForm }) {

    const getElement = (type) => {
        switch (type) {
            case "List": return List;
            default: return Element;
        }
    }

    return (
        <Block width={580}>
            <Formik
                initialValues={values}
                validateOnChange={false}
                validateOnBlur={false}
                validate={values => {
                    /**
                     * Turns a yup error path into the trail of labels the editor
                     * actually shows on screen, so
                     *   governanceSection.blocks[0].title
                     * reads as
                     *   Governança corporativa › Blocos › #1 › Título do bloco
                     *
                     * Without it the toast can only say how many fields are
                     * wrong, which on a page of nested lists leaves the editor
                     * opening every accordion to find them.
                     */
                    const describePath = (path) => {
                        const parts = String(path).split('.');
                        const labels = [];
                        let fields = null;

                        for (let i = 0; i < parts.length; i++) {
                            const match = parts[i].match(/^([^[]+)(?:\[(\d+)\])?$/);

                            if (!match) {
                                labels.push(parts[i]);
                                continue;
                            }

                            const key = match[1];
                            // Fallback for keys that have no field of their own -
                            // the title/url halves of a Button, for instance.
                            let label = key.charAt(0).toUpperCase() + key.slice(1);

                            if (i === 0) {
                                const section = structure.sections.find((item) => item.key === key);

                                if (section) {
                                    label = section.title || key;
                                    fields = section.properties.fields;
                                }
                            } else {
                                const field = fields && fields.find((item) => item.key === key);

                                label = field ? (field.title || key) : label;
                                fields = (field && field.properties && field.properties.fields) || null;
                            }

                            labels.push(label);

                            // Rows are numbered from 1 in the ui, not from 0.
                            if (match[2] !== undefined) {
                                labels.push(`#${Number(match[2]) + 1}`);
                            }
                        }

                        return labels.join(' › ');
                    };

                    const buildValidation = (fields) => {
                        const shape = {};

                        for (let i = 0; i < fields.length; i++) {
                            const field = fields[i];
                            switch (field.type) {
                                case "List":
                                    shape[field.key] = yup.array().of(yup.object(buildValidation(field.properties.fields)));
                                    break;
                                case "TextLine":
                                case "TextMultiLine":
                                case "TextEditor":
                                    shape[field.key] = yup.string();
                                    break;
                                case "File":
                                    shape[field.key] = yup.object();
                                    break;
                                case "Button":
                                    const innerShape = {
                                        title: yup.string(),
                                        // a link to a page on this site is written as /slug, which is
                                        // not a url - accept both rather than forcing an absolute one,
                                        // since an absolute url reloads the page instead of routing
                                        url: yup.string().test(
                                            'url-or-path',
                                            'Must be a full url or a path starting with /',
                                            (value) => !value || value.indexOf('/') === 0 || yup.string().url().isValidSync(value)
                                        )
                                    };

                                    if (field.properties) {
                                        if (field.properties.required) {
                                            innerShape.title = innerShape.title.required('This field is required');
                                            innerShape.url = innerShape.url.required('This field is required');
                                        }
                                    }

                                    shape[field.key] = yup.object().shape(innerShape);
                                    break;
                                default:
                                    shape[field.key] = yup.string();
                                    break;
                            }

                            if (field.properties && !["Button"].includes(field.type)) {
                                if (field.properties.required) {
                                    shape[field.key] = shape[field.key].required('This field is required').transform((function (value, originalValue) { if (value === null || value === undefined) { return "" } return originalValue; }));
                                }
                            }
                        }

                        return shape;
                    }

                    const shape = {};

                    for (let i = 0; i < structure.sections.length; i++) {
                        const section = structure.sections[i];
                        shape[section.key] = yup.object(buildValidation(section.properties.fields));
                    }

                    const schema = yup.object().shape(shape);
                    const errors = validateFormSchema(schema, values);
                    const errorsLength = Object.keys(errors).length;

                    if (errorsLength > 0) {
                        // Enough to act on without becoming a wall of text. The
                        // rest are still marked inline on the fields themselves.
                        const LISTED = 8;
                        const paths = Object.keys(errors);

                        toast.error(
                            <div>
                                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                                    {`Not saved — ${errorsLength} field${errorsLength === 1 ? '' : 's'} need attention:`}
                                </div>
                                {paths.slice(0, LISTED).map((path) => (
                                    <div key={path} style={{ lineHeight: 1.45 }}>
                                        {`• ${describePath(path)} — ${errors[path]}`}
                                    </div>
                                ))}
                                {paths.length > LISTED &&
                                    <div style={{ marginTop: 6, opacity: 0.85 }}>
                                        {`…and ${paths.length - LISTED} more, marked in red on the form.`}
                                    </div>
                                }
                            </div>,
                            // A list takes longer to read than "something went
                            // wrong", and it is useless if it vanishes first.
                            { autoHideDuration: Math.min(20000, 6000 + errorsLength * 1500) }
                        )
                    }

                    return errors;
                }}
                onSubmit={(values, { setSubmitting }) => {
                    setSubmitting(false);

                    const formatted = {};

                    for (let i = 0; i < structure.sections.length; i++) {
                        const section = structure.sections[i];
                        if (!values[section.key]) {
                            formatted[section.key] = {};
                        } else {
                            formatted[section.key] = values[section.key];
                        }
                    }

                    onSubmit && onSubmit(formatted);
                }}
            >
                {({
                    values,
                    errors,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    submitForm
                }) => {
                    bindSubmitForm && bindSubmitForm(submitForm);
                    return (
                        <form onSubmit={handleSubmit}>
                            <FieldArray
                                name="sections"
                                render={() => (
                                    structure.sections.map((section) => {
                                        return (
                                            <Block key={section.id}>
                                                <Section
                                                    title={section.title}
                                                >
                                                    <FieldArray
                                                        name={`${section.key}`}
                                                        render={() => {
                                                            if (section.properties) {
                                                                return (
                                                                    section.properties.fields.map((field, index) => {
                                                                        const Element = getElement(field.type);
                                                                        const first = index === 0;
                                                                        return (
                                                                            <Block key={field.id} mt={first ? 20 : 0} mb={52}>
                                                                                <Element
                                                                                    type={field.type}
                                                                                    path={`${section.key}.${field.key}`}
                                                                                    title={field.title}
                                                                                    fieldKey={field.key}
                                                                                    value={values[section.key] && values[section.key][field.key]}
                                                                                    errors={errors}
                                                                                    properties={field.properties}
                                                                                    getElement={getElement}
                                                                                    onChange={handleChange}
                                                                                    onBlur={handleBlur}
                                                                                />
                                                                            </Block>
                                                                        );
                                                                    })
                                                                );
                                                            }

                                                        }}
                                                    />
                                                </Section>
                                            </Block>
                                        )
                                    })
                                )}
                            />
                        </form>
                    )
                }}
            </Formik>
        </Block >
    );
}

export default ContentEditor;